/**
 * Helpers for WebAuthn / passkeys in the admin UI.
 * Server sends base64url strings; the browser needs ArrayBuffers.
 */

function bufferToBase64Url(buffer) {
    const bytes = buffer instanceof ArrayBuffer ? new Uint8Array(buffer) : new Uint8Array(buffer.buffer || buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i += 1) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function base64UrlToBuffer(base64url) {
    const padded = base64url.replace(/-/g, '+').replace(/_/g, '/');
    const pad = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
    const binary = atob(padded + pad);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
}

function revivePublicKeyOptions(publicKey) {
    const pk = { ...publicKey, challenge: base64UrlToBuffer(publicKey.challenge) };

    if (pk.user?.id) {
        pk.user = { ...pk.user, id: base64UrlToBuffer(pk.user.id) };
    }

    if (Array.isArray(pk.excludeCredentials)) {
        pk.excludeCredentials = pk.excludeCredentials.map((c) => ({
            ...c,
            id: base64UrlToBuffer(c.id),
        }));
    }

    if (Array.isArray(pk.allowCredentials)) {
        pk.allowCredentials = pk.allowCredentials.map((c) => ({
            ...c,
            id: base64UrlToBuffer(c.id),
        }));
    }

    return pk;
}

export function isWebAuthnAvailable() {
    return typeof window !== 'undefined' && !!window.PublicKeyCredential;
}

export async function createPasskey(publicKeyOptions) {
    const publicKey = revivePublicKeyOptions(publicKeyOptions);
    const credential = await navigator.credentials.create({ publicKey });
    if (!credential) {
        throw new Error('No credential created');
    }

    const response = credential.response;
    return {
        id: credential.id,
        rawId: bufferToBase64Url(credential.rawId),
        type: credential.type,
        clientExtensionResults: credential.getClientExtensionResults?.() || {},
        response: {
            clientDataJSON: bufferToBase64Url(response.clientDataJSON),
            attestationObject: bufferToBase64Url(response.attestationObject),
            transports: response.getTransports?.() || [],
        },
    };
}

export async function getPasskey(publicKeyOptions) {
    const publicKey = revivePublicKeyOptions(publicKeyOptions);
    const credential = await navigator.credentials.get({ publicKey });
    if (!credential) {
        throw new Error('No credential returned');
    }

    const response = credential.response;
    return {
        id: credential.id,
        rawId: bufferToBase64Url(credential.rawId),
        type: credential.type,
        clientExtensionResults: credential.getClientExtensionResults?.() || {},
        response: {
            clientDataJSON: bufferToBase64Url(response.clientDataJSON),
            authenticatorData: bufferToBase64Url(response.authenticatorData),
            signature: bufferToBase64Url(response.signature),
            userHandle: response.userHandle ? bufferToBase64Url(response.userHandle) : null,
        },
    };
}

export function isUserCancelledError(err) {
    const name = err?.name || '';
    return name === 'NotAllowedError' || name === 'AbortError';
}
